/**
 * Helpers para Sistema de Filtros Configurável
 * 
 * Facilita a criação e manipulação de filtros genéricos
 * que podem ser adaptados para diferentes domínios.
 */

import { getDomainConfig } from '../../config/product-domain'
import type { GenericProductFilter } from '../types/product'

/**
 * Cria filtros iniciais baseados na configuração do domínio
 */
export const createInitialFilters = (): GenericProductFilter => {
  const domainConfig = getDomainConfig()
  const filters: GenericProductFilter = {
    search: '',
    brand: [],
    category: [],
    price: {
      min: undefined,
      max: undefined
    },
    inStock: undefined,
    sortBy: 'price',
    sortOrder: 'asc'
  }

  // Adicionar filtros específicos do domínio
  if (domainConfig.domainSpecificFilters) {
    Object.keys(domainConfig.domainSpecificFilters).forEach(field => {
      const filterConfig = domainConfig.domainSpecificFilters![field]
      
      if (filterConfig.type === 'range') {
        filters[field] = {
          min: undefined,
          max: undefined
        }
      } else if (filterConfig.type === 'select') {
        filters[field] = []
      } else if (filterConfig.type === 'checkbox') {
        filters[field] = false
      }
    })
  }

  return filters
}

/**
 * Limpa todos os filtros
 */
export const clearFilters = (): GenericProductFilter => {
  return createInitialFilters()
}

/**
 * Verifica se há filtros ativos
 */
export const hasActiveFilters = (filters: GenericProductFilter): boolean => {
  if (filters.search && filters.search.trim() !== '') return true
  if (filters.brand && filters.brand.length > 0) return true
  if (filters.category && filters.category.length > 0) return true
  if (filters.price && (filters.price.min !== undefined || filters.price.max !== undefined)) return true
  if (filters.inStock !== undefined) return true

  // Verificar filtros específicos do domínio
  const domainConfig = getDomainConfig()
  if (domainConfig.domainSpecificFilters) {
    for (const field of Object.keys(domainConfig.domainSpecificFilters)) {
      const value = filters[field]
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        if (Array.isArray(value) && value.length > 0) return true
        if (typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) return true
        if (typeof value !== 'object' && !Array.isArray(value)) return true
      }
    }
  }

  return false
}

/**
 * Conta quantos filtros estão ativos
 */
export const countActiveFilters = (filters: GenericProductFilter): number => {
  let count = 0

  if (filters.search && filters.search.trim() !== '') count++
  if (filters.brand && filters.brand.length > 0) count++
  if (filters.category && filters.category.length > 0) count++
  if (filters.price && (filters.price.min !== undefined || filters.price.max !== undefined)) count++
  if (filters.inStock !== undefined) count++

  // Contar filtros específicos do domínio
  const domainConfig = getDomainConfig()
  if (domainConfig.domainSpecificFilters) {
    for (const field of Object.keys(domainConfig.domainSpecificFilters)) {
      const value = filters[field]
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        if (Array.isArray(value) && value.length > 0) {
          count++
        } else if (typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
          count++
        } else if (typeof value !== 'object' && !Array.isArray(value)) {
          count++
        }
      }
    }
  }

  return count
}

/**
 * Valida filtros
 */
export const validateFilters = (filters: GenericProductFilter): { valid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Validar preço
  if (filters.price) {
    if (filters.price.min !== undefined && filters.price.min < 0) {
      errors.push('Preço mínimo não pode ser negativo')
    }
    if (filters.price.max !== undefined && filters.price.max < 0) {
      errors.push('Preço máximo não pode ser negativo')
    }
    if (
      filters.price.min !== undefined &&
      filters.price.max !== undefined &&
      filters.price.min > filters.price.max
    ) {
      errors.push('Preço mínimo não pode ser maior que o máximo')
    }
  }

  // Validar filtros específicos do domínio
  const domainConfig = getDomainConfig()
  if (domainConfig.domainSpecificFilters) {
    Object.keys(domainConfig.domainSpecificFilters).forEach(field => {
      const value = filters[field]
      const filterConfig = domainConfig.domainSpecificFilters![field]

      if (filterConfig.type === 'range' && typeof value === 'object') {
        if (value.min !== undefined && value.min < 0) {
          errors.push(`${filterConfig.label} mínimo não pode ser negativo`)
        }
        if (value.max !== undefined && value.max < 0) {
          errors.push(`${filterConfig.label} máximo não pode ser negativo`)
        }
        if (
          value.min !== undefined &&
          value.max !== undefined &&
          value.min > value.max
        ) {
          errors.push(`${filterConfig.label} mínimo não pode ser maior que o máximo`)
        }
      }
    })
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Serializa filtros para URL (query params)
 */
export const serializeFilters = (filters: GenericProductFilter): Record<string, string> => {
  const params: Record<string, string> = {}

  if (filters.search) params.search = filters.search
  if (filters.brand && filters.brand.length > 0) params.brand = filters.brand.join(',')
  if (filters.category && filters.category.length > 0) params.category = filters.category.join(',')
  if (filters.price) {
    if (filters.price.min !== undefined) params.priceMin = String(filters.price.min)
    if (filters.price.max !== undefined) params.priceMax = String(filters.price.max)
  }
  if (filters.inStock !== undefined) params.inStock = String(filters.inStock)
  if (filters.sortBy) params.sortBy = filters.sortBy
  if (filters.sortOrder) params.sortOrder = filters.sortOrder

  // Serializar filtros específicos do domínio
  const domainConfig = getDomainConfig()
  if (domainConfig.domainSpecificFilters) {
    Object.keys(domainConfig.domainSpecificFilters).forEach(field => {
      const value = filters[field]
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params[field] = value.join(',')
        } else if (typeof value === 'object') {
          if (value.min !== undefined) params[`${field}Min`] = String(value.min)
          if (value.max !== undefined) params[`${field}Max`] = String(value.max)
        } else {
          params[field] = String(value)
        }
      }
    })
  }

  return params
}

/**
 * Deserializa filtros de URL (query params)
 */
export const deserializeFilters = (params: URLSearchParams): GenericProductFilter => {
  const filters = createInitialFilters()

  if (params.get('search')) filters.search = params.get('search') || ''
  if (params.get('brand')) filters.brand = params.get('brand')?.split(',') || []
  if (params.get('category')) filters.category = params.get('category')?.split(',') || []
  if (params.get('priceMin') || params.get('priceMax')) {
    filters.price = {
      min: params.get('priceMin') ? Number(params.get('priceMin')) : undefined,
      max: params.get('priceMax') ? Number(params.get('priceMax')) : undefined
    }
  }
  if (params.get('inStock')) filters.inStock = params.get('inStock') === 'true'
  if (params.get('sortBy')) filters.sortBy = params.get('sortBy') || 'price'
  if (params.get('sortOrder')) filters.sortOrder = params.get('sortOrder') as 'asc' | 'desc' || 'asc'

  // Deserializar filtros específicos do domínio
  const domainConfig = getDomainConfig()
  if (domainConfig.domainSpecificFilters) {
    Object.keys(domainConfig.domainSpecificFilters).forEach(field => {
      const filterConfig = domainConfig.domainSpecificFilters![field]
      
      if (filterConfig.type === 'select') {
        const value = params.get(field)
        if (value) filters[field] = value.split(',')
      } else if (filterConfig.type === 'range') {
        const min = params.get(`${field}Min`)
        const max = params.get(`${field}Max`)
        if (min || max) {
          filters[field] = {
            min: min ? Number(min) : undefined,
            max: max ? Number(max) : undefined
          }
        }
      } else if (filterConfig.type === 'checkbox') {
        const value = params.get(field)
        if (value) filters[field] = value === 'true'
      }
    })
  }

  return filters
}

